import { useState, useCallback, useMemo } from 'react'
import { Trash2, ChevronDown, ChevronRight, CheckCircle, Clock, Loader, XCircle } from 'lucide-react'
import type { AppStatus } from '../store/appsSlice'
import type { TaskInfo } from '../store/activitySlice'
import { fetchTaskLogs, removeTask } from '../store/activitySlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import './TaskTimeline.css'

interface TaskTimelineProps {
  tasks: TaskInfo[]
  loading: boolean
  error: string | null
  appId: string
  appStatus: AppStatus
  onRefresh: () => void
}

function extractSkillName(skill: string | undefined | null): string {
  if (!skill) return 'prompt'
  const base = skill.split('/').pop() || skill
  return base.replace(/\.md$/, '')
}

function formatTime(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function formatStartTime(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function formatDuration(startTs: string | null, endTs: string | null): string {
  if (!startTs) return ''
  if (!endTs) return 'In progress...'
  const start = new Date(startTs).getTime()
  const end = new Date(endTs).getTime()
  const diffMs = end - start
  if (diffMs < 1000) return '<1s'
  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  if (hours > 0) return `${hours}h ${minutes % 60}m`
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`
  return `${seconds}s`
}

const statusConfig: Record<string, { icon: typeof CheckCircle; label: string }> = {
  queued: { icon: Clock, label: 'Queued' },
  in_progress: { icon: Loader, label: 'Running' },
  completed: { icon: CheckCircle, label: 'Done' },
  failed: { icon: XCircle, label: 'Failed' },
}

function formatFullTime(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function TaskItem({ task, allTasks, appId, isSignedIn, onDelete, deleteError }: { task: TaskInfo; allTasks: TaskInfo[]; appId: string; isSignedIn: boolean; onDelete: (taskId: string) => void; deleteError?: string }) {
  const dispatch = useAppDispatch()
  const logs = useAppSelector((state) => state.activity.taskLogs[task.id])
  const logsLoading = useAppSelector((state) => state.activity.taskLogsLoading[task.id])
  const [expanded, setExpanded] = useState(false)

  const subtasks = Array.isArray(task.subtasks) ? task.subtasks : []
  const config = statusConfig[task.status] || statusConfig.queued
  const Icon = config.icon
  const hasLogs = task.log_count > 0
  const skillName = extractSkillName(task.skill)
  const isQueued = task.status === 'queued'
  const parentSkillName = task.parent_skill ? extractSkillName(task.parent_skill) : null

  const duration = task.status === 'in_progress'
    ? 'In progress...'
    : formatDuration(task.started_at, task.completed_at)

  const blockedInfo = useMemo(() => {
    if (task.status !== 'queued') return null
    const requiredIds = task.required_task_ids
    if (!requiredIds || requiredIds.length === 0) return { ready: true, blockedBy: [] }
    const taskMap = new Map(allTasks.map((t) => [t.id, t]))
    const blockedBy: string[] = []
    for (const reqId of requiredIds) {
      const req = taskMap.get(reqId)
      if (!req || req.status !== 'completed') {
        blockedBy.push(req ? extractSkillName(req.skill) : reqId)
      }
    }
    return { ready: blockedBy.length === 0, blockedBy }
  }, [task.status, task.required_task_ids, allTasks])

  const handleToggle = useCallback(() => {
    if (!expanded && hasLogs && !logs && !logsLoading) {
      dispatch(fetchTaskLogs({ appId, taskId: task.id }))
    }
    setExpanded(!expanded)
  }, [expanded, hasLogs, logs, logsLoading, dispatch, appId, task.id])

  return (
    <div className="task-item" data-testid="task-timeline-item" data-task-status={task.status}>
      <div className="task-item__header-row">
        <button
          className="task-item__header"
          onClick={handleToggle}
          aria-expanded={expanded}
          data-testid="task-timeline-item-header"
        >
          <div className="task-item__header-left">
            {expanded ? (
              <ChevronDown size={16} className="task-item__chevron" aria-hidden="true" />
            ) : (
              <ChevronRight size={16} className="task-item__chevron" aria-hidden="true" />
            )}
            <Icon size={16} className={`task-item__status-icon`} aria-hidden="true" />
            <span
              className={`task-item__status-badge task-item__status-badge--${task.status}`}
              data-testid="task-timeline-item-status"
            >
              {config.label}
            </span>
            <span className="task-item__skill" data-testid="task-timeline-item-skill">
              {skillName}
            </span>
          </div>
          <div className="task-item__meta">
            <span className="task-item__time" data-testid="task-timeline-item-time">
              {formatStartTime(task.started_at || task.created_at)}
            </span>
            {duration && (
              <span className="task-item__duration" data-testid="task-timeline-item-duration">
                {duration}
              </span>
            )}
          </div>
        </button>
        {isQueued && isSignedIn && (
          <button
            className="task-item__delete-btn"
            onClick={() => onDelete(task.id)}
            aria-label={`Delete task ${skillName}`}
            data-testid="delete-task-btn"
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      {deleteError && (
        <p className="task-item__delete-error" role="alert" data-testid="delete-task-error">
          {deleteError}
        </p>
      )}

      {expanded && (
        <div className="task-item__details" data-testid="task-timeline-item-details">
          <div className="task-item__detail-row">
            <span className="task-item__detail-label">Created</span>
            <span className="task-item__detail-value">{formatFullTime(task.created_at)}</span>
          </div>
          {parentSkillName && (
            <div className="task-item__detail-row">
              <span className="task-item__detail-label">Parent task</span>
              <span className="task-item__detail-value">{parentSkillName}</span>
            </div>
          )}
          {blockedInfo && (
            <div className="task-item__detail-row" data-testid="task-timeline-item-blocked-info">
              <span className="task-item__detail-label">Status</span>
              {blockedInfo.ready ? (
                <span className="task-item__detail-value task-item__detail-value--ready" data-testid="task-blocked-status-ready">Ready</span>
              ) : (
                <span className="task-item__detail-value task-item__detail-value--blocked" data-testid="task-blocked-status-blocked">
                  Blocked by {blockedInfo.blockedBy.join(', ')}
                </span>
              )}
            </div>
          )}
          {task.prompt && (
            <div className="task-item__prompt" data-testid="task-timeline-item-prompt">
              <span className="task-item__detail-label">Prompt</span>
              <p className="task-item__prompt-text">{task.prompt}</p>
            </div>
          )}
        </div>
      )}

      {subtasks.length > 0 && (
        <ul className="task-item__subtasks" data-testid="task-timeline-subtasks">
          {subtasks.map((st, i) => (
            <li key={i} className="task-item__subtask" data-testid="task-timeline-subtask">
              {st}
            </li>
          ))}
        </ul>
      )}

      {expanded && hasLogs && (
        logsLoading ? (
          <div className="task-item__logs-loading" data-testid="task-timeline-logs-loading">
            Loading logs...
          </div>
        ) : logs ? (
          <div className="task-item__logs" data-testid="task-timeline-logs">
            {logs.map((log) => (
              <div key={log.id} className="task-item__log-entry" data-testid="task-timeline-log-entry">
                <span className="task-item__log-time">
                  {formatTime(log.timestamp)}
                </span>
                <span className="task-item__log-message">
                  {log.message}
                </span>
              </div>
            ))}
          </div>
        ) : null
      )}
    </div>
  )
}

function groupBySkill(tasks: TaskInfo[]): Map<string, TaskInfo[]> {
  const map = new Map<string, TaskInfo[]>()
  for (const task of tasks) {
    const skill = extractSkillName(task.skill)
    const group = map.get(skill) || []
    group.push(task)
    map.set(skill, group)
  }
  return map
}

interface CollapsibleSectionProps {
  title: string
  count: number
  testId: string
  defaultOpen?: boolean
  children: React.ReactNode
}

function CollapsibleSection({ title, count, testId, defaultOpen = false, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="task-section" data-testid={testId}>
      <button
        className="task-section__toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        {open ? (
          <ChevronDown size={14} className="task-section__toggle-icon" aria-hidden="true" />
        ) : (
          <ChevronRight size={14} className="task-section__toggle-icon" aria-hidden="true" />
        )}
        <span className="task-section__title">{title}</span>
        <span className="task-section__count" data-testid={`${testId}-count`}>{count}</span>
      </button>
      {open && (
        <div className="task-section__body" data-testid={`${testId}-body`}>
          {children}
        </div>
      )}
    </div>
  )
}

interface SkillGroupProps {
  skillName: string
  tasks: TaskInfo[]
  allTasks: TaskInfo[]
  appId: string
  isSignedIn: boolean
  onDelete: (taskId: string) => void
  deleteErrors: Record<string, string>
  parentTestId: string
}

function getLatestTime(tasks: TaskInfo[]): string | null {
  let latest: string | null = null
  for (const task of tasks) {
    const ts = task.completed_at || task.created_at
    if (!latest || ts > latest) latest = ts
  }
  return latest
}

function SkillGroup({ skillName, tasks, allTasks, appId, isSignedIn, onDelete, deleteErrors, parentTestId }: SkillGroupProps) {
  const [open, setOpen] = useState(false)
  const latestTime = useMemo(() => getLatestTime(tasks), [tasks])
  return (
    <div className="task-skill-group" data-testid={`${parentTestId}-skill-group`}>
      <button
        className="task-skill-group__toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`${parentTestId}-skill-group-toggle`}
      >
        {open ? (
          <ChevronDown size={12} className="task-skill-group__toggle-icon" aria-hidden="true" />
        ) : (
          <ChevronRight size={12} className="task-skill-group__toggle-icon" aria-hidden="true" />
        )}
        <span className="task-skill-group__name" data-testid={`${parentTestId}-skill-group-name`}>{skillName}</span>
        <span className="task-skill-group__count">({tasks.length})</span>
        {latestTime && (
          <span className="task-skill-group__time" data-testid={`${parentTestId}-skill-group-time`}>
            {formatStartTime(latestTime)}
          </span>
        )}
      </button>
      {open && (
        <div className="task-skill-group__tasks" data-testid={`${parentTestId}-skill-group-tasks`}>
          {tasks.map((task) => (
            <TaskItem key={task.id} task={task} allTasks={allTasks} appId={appId} isSignedIn={isSignedIn} onDelete={onDelete} deleteError={deleteErrors[task.id]} />
          ))}
        </div>
      )}
    </div>
  )
}

function TaskTimeline({ tasks, loading, error, appId, appStatus, onRefresh }: TaskTimelineProps) {
  const dispatch = useAppDispatch()
  const isSignedIn = useAppSelector((state) => !!state.auth.user)
  const polling = useAppSelector((state) => state.activity.polling)
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({})

  const handleDelete = useCallback(async (taskId: string) => {
    setDeleteErrors((prev) => {
      const next = { ...prev }
      delete next[taskId]
      return next
    })
    try {
      const resp = await fetch('/api/delete-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId }),
      })
      if (resp.ok) {
        dispatch(removeTask(taskId))
        onRefresh()
      } else {
        let message = 'Failed to delete task'
        try {
          const data = await resp.json()
          if (data.error) message = data.error
        } catch { /* use default message */ }
        setDeleteErrors((prev) => ({ ...prev, [taskId]: message }))
      }
    } catch {
      setDeleteErrors((prev) => ({ ...prev, [taskId]: 'Failed to delete task' }))
    }
  }, [dispatch, onRefresh])

  const { runningTasks, queuedTasks, finishedTasks } = useMemo(() => {
    const running: TaskInfo[] = []
    const queued: TaskInfo[] = []
    const finished: TaskInfo[] = []
    for (const task of tasks) {
      if (task.status === 'in_progress') running.push(task)
      else if (task.status === 'queued') queued.push(task)
      else finished.push(task)
    }
    // Sort running by started_at desc
    running.sort((a, b) => {
      const aTime = new Date(a.started_at ?? a.created_at).getTime()
      const bTime = new Date(b.started_at ?? b.created_at).getTime()
      return bTime - aTime
    })
    // Sort queued by created_at asc (first in queue first)
    queued.sort((a, b) => {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })
    // Sort finished by completed_at desc (most recent first)
    finished.sort((a, b) => {
      const aTime = new Date(a.completed_at ?? a.created_at).getTime()
      const bTime = new Date(b.completed_at ?? b.created_at).getTime()
      return bTime - aTime
    })
    return { runningTasks: running, queuedTasks: queued, finishedTasks: finished }
  }, [tasks])

  const queuedBySkill = useMemo(() => groupBySkill(queuedTasks), [queuedTasks])
  const finishedBySkill = useMemo(() => groupBySkill(finishedTasks), [finishedTasks])

  const hasContent = tasks.length > 0

  return (
    <section className="task-timeline" data-testid="task-timeline" aria-labelledby="task-timeline-heading">
      <div className="task-timeline__header">
        <h2 className="task-timeline__title" id="task-timeline-heading">Build Tasks</h2>
        {polling && (
          <span className="task-timeline__auto-refresh" data-testid="task-timeline-auto-refresh" aria-live="polite">
            <span className="task-timeline__auto-refresh-dot" aria-hidden="true" />
            Auto-refreshing
          </span>
        )}
      </div>
      <div className="task-timeline__list" data-testid="task-timeline-list" aria-live="polite">
        {loading ? (
          <div className="task-timeline__loading" data-testid="task-timeline-loading" role="status">
            Loading tasks...
          </div>
        ) : error ? (
          <div className="task-timeline__error" data-testid="task-timeline-error" role="alert">
            {error}
          </div>
        ) : !hasContent ? (
          <div className="task-timeline__empty" data-testid="task-timeline-empty">
            {appStatus === 'queued'
              ? 'Development has not started yet. Tasks will appear here once building begins.'
              : 'No tasks recorded.'}
          </div>
        ) : (
          <>
            {runningTasks.length > 0 && (
              <div className="task-section" data-testid="task-section-running">
                <div className="task-section__label" data-testid="task-section-running-label">
                  <Loader size={14} className="task-section__label-icon task-section__label-icon--running" aria-hidden="true" />
                  <span className="task-section__title">Running Tasks</span>
                  <span className="task-section__count" data-testid="task-section-running-count">{runningTasks.length}</span>
                </div>
                <div className="task-section__body" data-testid="task-section-running-body">
                  {runningTasks.map((task) => (
                    <TaskItem key={task.id} task={task} allTasks={tasks} appId={appId} isSignedIn={isSignedIn} onDelete={handleDelete} deleteError={deleteErrors[task.id]} />
                  ))}
                </div>
              </div>
            )}

            {queuedTasks.length > 0 && (
              <CollapsibleSection
                title="Queued Tasks"
                count={queuedTasks.length}
                testId="task-section-queued"
                defaultOpen={false}
              >
                {Array.from(queuedBySkill.entries()).map(([skillName, skillTasks]) => (
                  <SkillGroup
                    key={skillName}
                    skillName={skillName}
                    tasks={skillTasks}
                    allTasks={tasks}
                    appId={appId}
                    isSignedIn={isSignedIn}
                    onDelete={handleDelete}
                    deleteErrors={deleteErrors}
                    parentTestId="task-section-queued"
                  />
                ))}
              </CollapsibleSection>
            )}

            {finishedTasks.length > 0 && (
              <CollapsibleSection
                title="Finished Tasks"
                count={finishedTasks.length}
                testId="task-section-finished"
                defaultOpen={false}
              >
                {Array.from(finishedBySkill.entries()).map(([skillName, skillTasks]) => (
                  <SkillGroup
                    key={skillName}
                    skillName={skillName}
                    tasks={skillTasks}
                    allTasks={tasks}
                    appId={appId}
                    isSignedIn={isSignedIn}
                    onDelete={handleDelete}
                    deleteErrors={deleteErrors}
                    parentTestId="task-section-finished"
                  />
                ))}
              </CollapsibleSection>
            )}
          </>
        )}
      </div>
    </section>
  )
}

export default TaskTimeline
